/* eslint-disable prefer-arrow-callback */

import React, { useCallback, useMemo } from 'react';
import { shallowEqual } from 'react-redux';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getEventStats } from '../../store/eventStats/thunks';
import { selectCurrentStore, selectStoreServices } from '../../store/store/selectors';
import { TimeHelper } from '../../utils';
import { convertDaySummaryToCalendarDataType } from '../commons/services/calendarOverviewService';
import { IWeek, DayContentSlot } from './types';
import { OverviewHeader, CalendarOverviewGrid, CalendarDay } from './components';

import styles from './CalendarOverview.module.scss';

type Props = {
  date: number;
  onClose: () => void;
};

const CalendarDayContent: DayContentSlot = React.memo(function CalendarDayContentComponent({ date, dateKey }) {
  const store = useAppSelector(selectCurrentStore);
  const services = useAppSelector(selectStoreServices);
  const dayEventStats = useAppSelector((state) => state.eventStats.data[dateKey], shallowEqual);

  const dayData = useMemo(() => {
    if (!dayEventStats) {
      return null;
    }

    return convertDaySummaryToCalendarDataType(dayEventStats, services);
  }, [dayEventStats, services]);

  if (!dayData) {
    return null;
  }

  return (
    <CalendarDay
      currentDate={TimeHelper.getNowDateUnix(store.timezone)}
      date={date}
      data={dayData}
      timezone={store.timezone}
    />
  );
});

export function CalendarOverview({ date, onClose }: Props) {
  const dispatch = useAppDispatch();

  const store = useAppSelector(selectCurrentStore);
  const eventStatsState = useAppSelector((state) => state.eventStats, shallowEqual);

  const onLoadData = useCallback(async (weeks: IWeek[]) => {
    const from = weeks[0].startDate;
    const to = weeks[weeks.length - 1].endDate;

    if (!eventStatsState.queryHaveResponse[`${from}-${to}`]) {
      await dispatch(
        getEventStats({
          from,
          to,
          storeId: store.id,
          enterpriseId: store.enterpriseId,
        }),
      ).unwrap();
    }
  }, []);

  return (
    <div className={styles.calendar_overview_wrapper}>
      <OverviewHeader date={date} onBackClick={onClose} timezone={store.timezone} />
      <CalendarOverviewGrid
        initialDate={date}
        timezone={store.timezone}
        onLoadData={onLoadData}
        DayContentSlot={CalendarDayContent}
      />
    </div>
  );
}
